
import React, { useState, useEffect } from "react";
import { getManualFileName } from "../utils/manualUtils";

import {
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import { login, getUserFoldersAndFiles } from "../api";
import { useNavigate } from "react-router-dom";
import {
  maxLoginLength,
  maxPasswordLength,
  validateUserInput,
} from "../utils/loginUtils";
import {
  loginContainer,
  paperStyle,
  upperSection,
  lowerSection,
  buttonContainer,
  logoAndTitleContainer,
  logoStyle,
  titleContainer,
  smallButtonStyle,
  logoStyleGDU,
} from "../styles/loginStyles";
import "../styles/styles.css";
import { useUserContext } from "../Context/UserContext";
import { resolveCaptchaProvider } from "../captcha/CaptchaProvider";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const Login: React.FC = () => {
  const [user, setUser] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [userWarning, setUserWarning] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true);
  const [versionInfo, setVersionInfo] = useState<string>("");
  const captchaProvider = resolveCaptchaProvider();
  const Captcha = captchaProvider.Component;
  const [captchaOpen, setCaptchaOpen] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(false);

  const navigate = useNavigate();
  const { setUserAccessPath } = useUserContext();

  useEffect(() => {
    fetch("/sobre.txt")
      .then((response) => response.text())
      .then((text) => {
        const versionMatch = text.match(/VERSAO:\s*(.*)/);
        const buildMatch = text.match(/BUILD:\s*(.*)/);

        if (versionMatch && buildMatch) {
          setVersionInfo(`Versão ${versionMatch[1]} • Build ${buildMatch[1]}`);
        } else {
          setVersionInfo("Versão não encontrada");
        }
      })
      .catch((error) => {
        console.error("Erro ao carregar versão:", error);
        setVersionInfo("Erro ao carregar versão");
      });
  }, []);

  useEffect(() => {
    const isUserValid = user.trim() !== "" && !validateUserInput(user);
    const isPasswordFilled = password.trim() !== "";

    setIsButtonDisabled(!(isUserValid && isPasswordFilled));
  }, [user, password]);

  const handleMouseDown = () => setShowPassword(true);
  const handleMouseUp = () => setShowPassword(false);

  const handleUserChange = (value: string) => {
    if (value.length <= maxLoginLength) setUser(value);

    if (errorMessage) setErrorMessage(null);

    const warning = validateUserInput(value);
    if (value.length === maxLoginLength) {
      setUserWarning(`Máximo de ${maxLoginLength} caracteres atingido.`);
    } else {
      setUserWarning(warning || null);
    }
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    if (errorMessage) setErrorMessage(null);
  };

  const handleSubmit = async (token: string) => {
    setIsLoading(true);
    setErrorMessage(null);

    try {
      const authResponse = await login(user, password, token);

      await delay(500);

      const { user: userData, token: jwt } = authResponse;

      sessionStorage.setItem("token", jwt);
      sessionStorage.setItem("username", userData.login);
      sessionStorage.setItem("user", JSON.stringify(userData));

      const sharedItems = await getUserFoldersAndFiles(userData.login);
      setUserAccessPath(sharedItems);

      navigate(userData.type === 99 ? "/admin" : "/user");
    } catch (error: any) {
      setErrorMessage(error.message || "Erro ao realizar login");
      captchaProvider.reset?.();
    } finally {
      setIsLoading(false);
    }
  };

  const handleCaptchaSuccess = async (token: string) => {
    if (!token) return;

    setCaptchaOpen(false);
    setPendingSubmit(false);

    await handleSubmit(token);
  };

  // const handleSubmit = async () => {
  //   setErrorMessage(null);
  //   setIsLoading(true);

  //   try {
  //     // 🔥 Envia token do reCAPTCHA para o backend
  //     const authResponse = await login(user, password, captchaToken);

  //     await delay(1000);

  //     const { user: userData, token } = authResponse;

  //     if (!userData || !token) {
  //       setErrorMessage(
  //         "Falha no login: dados inválidos recebidos do servidor."
  //       );
  //       return;
  //     }

  //     sessionStorage.setItem("token", token);
  //     sessionStorage.setItem("username", userData.login);
  //     sessionStorage.setItem("user", JSON.stringify(userData));

  //     userData.creationDate = new Date(userData.creationDate).toLocaleString();

  //     const sharedItems = await getUserFoldersAndFiles(userData.login);
  //     setUserAccessPath(sharedItems);

  //     if (userData.type === 99) {
  //       navigate("/admin");
  //     } else {
  //       try {
  //         const fixedItems =
  //           sharedItems.length === 0
  //             ? []
  //             : sharedItems.map((item: { path: string }) => ({
  //                 ...item,
  //                 path: item.path.replace(/\\\\/g, "\\"),
  //               }));

  //         sessionStorage.setItem("userFolders", JSON.stringify(fixedItems));
  //       } catch {
  //         sessionStorage.setItem("userFolders", JSON.stringify([]));
  //       } finally {
  //         navigate("/user");
  //       }
  //     }
  //   } catch (error: any) {
  //     console.error("Erro no login:", error);

  //     let message = "Erro inesperado. Tente novamente.";

  //     if (typeof error === "string") {
  //       message = error;
  //     } else if (error?.response?.data?.message) {
  //       message = error.response.data.message;
  //     } else if (typeof error?.message === "string") {
  //       message = error.message;
  //     }

  //     setErrorMessage(message);

  //     // 🔁 reseta captcha SEMPRE
  //     setCaptchaToken("");
  //     captchaProvider.reset?.();
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const handleLoginClick = () => {
    setErrorMessage(null);

    // valida usuário e senha antes de abrir captcha
    const isUserValid = user.trim() !== "" && !validateUserInput(user);
    const isPasswordFilled = password.trim() !== "";

    if (!isUserValid || !isPasswordFilled) {
      setErrorMessage("Preencha usuário e senha corretamente.");
      return;
    }

    // abre o popup de captcha
    setCaptchaOpen(true);
    setPendingSubmit(true);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" && !isButtonDisabled && !isLoading) {
      handleLoginClick();
    }
  };

  return (
    <>
      <Backdrop open={isLoading} style={{ zIndex: 1200, color: "#fff" }}>
        <CircularProgress size={60} color="inherit" />
      </Backdrop>

      <Box sx={loginContainer} onKeyDown={handleKeyDown} tabIndex={0}>
        <Paper elevation={3} sx={paperStyle}>
          <Box sx={upperSection}>
            <Box sx={logoAndTitleContainer}>
              <img src="/logo.png" alt="Logo" style={logoStyle} />
              <Box sx={titleContainer}>
                <img
                  src="/gdu2.0b.png"
                  alt="GDU 2.0.png"
                  style={logoStyleGDU}
                />
              </Box>
            </Box>
          </Box>

          <form onSubmit={(e) => e.preventDefault()}>
            <Box sx={lowerSection}>
              {errorMessage && (
                <Typography
                  color="error"
                  variant="body2"
                  align="center"
                  marginTop={2}
                >
                  {errorMessage}
                </Typography>
              )}

              <TextField
                fullWidth
                label="Usuário"
                placeholder="Digite seu usuário"
                variant="outlined"
                margin="normal"
                value={user}
                onChange={(e) => handleUserChange(e.target.value)}
                error={!!validateUserInput(user)}
                helperText={userWarning}
                inputProps={{ maxLength: maxLoginLength }}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                fullWidth
                label="Senha"
                placeholder="Digite sua senha"
                variant="outlined"
                margin="normal"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => handlePasswordChange(e.target.value)}
                error={password.length > maxPasswordLength}
                helperText={
                  password.length > maxPasswordLength
                    ? `Máximo de ${maxPasswordLength} caracteres atingido.`
                    : ""
                }
                inputProps={{ maxLength: maxPasswordLength }}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onMouseDown={handleMouseDown}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={buttonContainer} mt={2}>
                <Button
                  variant="contained"
                  sx={smallButtonStyle}
                  disabled={isButtonDisabled || isLoading}
                  fullWidth
                  onClick={handleLoginClick}
                >
                  {isLoading ? <RotateLeftIcon /> : "PROSSEGUIR COM LOGON"}
                </Button>
              </Box>
            </Box>
          </form>

          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              px: 2,
            }}
          >
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ mt: 1, fontSize: "0.7rem" }}
            >
              Para mais informações,{" "}
              <a
                href={`/modelos/${getManualFileName()}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: "inherit",
                  textDecoration: "underline",
                  fontWeight: 500,
                }}
              >
                acesse o manual aqui
              </a>
              .
            </Typography>

            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ mt: 0.5, mb: 1, fontSize: "0.7rem" }}
            >
              {versionInfo}
            </Typography>
          </Box>
        </Paper>
      </Box>
      <Dialog open={captchaOpen} disableEscapeKeyDown>
        <DialogTitle>Verificação de segurança</DialogTitle>

        <DialogContent
          sx={{ display: "flex", justifyContent: "center", mt: 2 }}
        >
          <Captcha onToken={handleCaptchaSuccess} />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setCaptchaOpen(false);
              captchaProvider.reset?.();
            }}
          >
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Login;
